import {
  DateType,
  DemandByTypeType,
  DemandTypeNameType,
  LocationType,
  ScenarioLoadAdditionType,
} from "../Types";
import { DAYS_PER_YEAR, TICK_MINUTES, TICKS_PER_DAY } from "../Constants";
import { pow } from "../helpers/Pow";
import { cos, exp } from "../helpers/Libm";

export const DEMAND_TYPES: readonly DemandTypeNameType[] = [
  "Residential",
  "Commercial",
  "Industrial",
  "Transportation",
  "Mining",
  "Data Centers",
] as const;

// The two authored end uses sit outside the regional mix. A region has no characteristic share
// of either: a grid has the mine or the campus on it or it does not, and how big it is comes
// from the scenario rather than from how many customers the utility serves.
type OrganicDemandType = Exclude<DemandTypeNameType, "Data Centers" | "Mining">;
type SectorMix = Record<OrganicDemandType, number>;
type GrowthProfile = Record<OrganicDemandType, number>;

// EIA divides end-use demand into residential, commercial, industrial, and transportation.
// These broad regional mixes preserve that standard while Data Centers are pulled out of the
// commercial sector below. They are intentionally scenario-scale profiles rather than a claim
// that every city in a region has an identical measured mix.
// https://www.eia.gov/tools/faqs/faq.php?id=447&t=1
const REGION_MIX: Record<string, SectorMix> = {
  "North America": {
    Residential: 0.39,
    Commercial: 0.34,
    Industrial: 0.265,
    Transportation: 0.005,
  },
  "South America": {
    Residential: 0.35,
    Commercial: 0.24,
    Industrial: 0.4,
    Transportation: 0.01,
  },
  Europe: {
    Residential: 0.33,
    Commercial: 0.31,
    Industrial: 0.34,
    Transportation: 0.02,
  },
  Africa: {
    Residential: 0.5,
    Commercial: 0.2,
    Industrial: 0.29,
    Transportation: 0.01,
  },
  "Middle East": {
    Residential: 0.38,
    Commercial: 0.3,
    Industrial: 0.31,
    Transportation: 0.01,
  },
  // Soviet-era metals, mining and chemicals still anchor these grids, so industry takes a larger
  // share than in the Gulf and the commercial sector a smaller one.
  "Central Asia": {
    Residential: 0.32,
    Commercial: 0.21,
    Industrial: 0.46,
    Transportation: 0.01,
  },
  Caucasus: {
    Residential: 0.4,
    Commercial: 0.26,
    Industrial: 0.32,
    Transportation: 0.02,
  },
  "South Asia": {
    Residential: 0.38,
    Commercial: 0.21,
    Industrial: 0.4,
    Transportation: 0.01,
  },
  "East Asia": {
    Residential: 0.22,
    Commercial: 0.25,
    Industrial: 0.52,
    Transportation: 0.01,
  },
  "Southeast Asia": {
    Residential: 0.32,
    Commercial: 0.24,
    Industrial: 0.43,
    Transportation: 0.01,
  },
  Oceania: {
    Residential: 0.37,
    Commercial: 0.32,
    Industrial: 0.29,
    Transportation: 0.02,
  },
};

// Annual change in load per customer. Customer growth remains the common demographic baseline;
// these rates create the issue's location-sensitive structural change on top of it: mature
// regions can flatten, industrial load can retreat, and electrified transport can grow.
const REGION_GROWTH: Record<string, GrowthProfile> = {
  "North America": {
    Residential: 0,
    Commercial: 0.002,
    Industrial: -0.002,
    Transportation: 0.025,
  },
  "South America": {
    Residential: 0.004,
    Commercial: 0.006,
    Industrial: 0.005,
    Transportation: 0.02,
  },
  Europe: {
    Residential: -0.003,
    Commercial: 0,
    Industrial: -0.005,
    Transportation: 0.025,
  },
  Africa: {
    Residential: 0.012,
    Commercial: 0.015,
    Industrial: 0.014,
    Transportation: 0.025,
  },
  "Middle East": {
    Residential: 0.008,
    Commercial: 0.01,
    Industrial: 0.009,
    Transportation: 0.025,
  },
  "Central Asia": {
    Residential: 0.009,
    Commercial: 0.012,
    Industrial: 0.01,
    Transportation: 0.025,
  },
  Caucasus: {
    Residential: 0.005,
    Commercial: 0.008,
    Industrial: 0.004,
    Transportation: 0.025,
  },
  "South Asia": {
    Residential: 0.012,
    Commercial: 0.014,
    Industrial: 0.015,
    Transportation: 0.025,
  },
  "East Asia": {
    Residential: -0.003,
    Commercial: 0.004,
    Industrial: 0.006,
    Transportation: 0.025,
  },
  "Southeast Asia": {
    Residential: 0.01,
    Commercial: 0.013,
    Industrial: 0.015,
    Transportation: 0.025,
  },
  Oceania: {
    Residential: 0.005,
    Commercial: 0.006,
    Industrial: 0.002,
    Transportation: 0.03,
  },
};

const DEFAULT_MIX = REGION_MIX["North America"];
const DEFAULT_GROWTH = REGION_GROWTH["North America"];

// These are historical/projected shares of total U.S. electricity, linearly interpolated between
// published observations. 2000 (28.2 TWh) and 2006 (61.4 TWh) are from DOE's data-center energy
// profile; 2014, 2018, 2023 and the 2028 midpoint are from Berkeley Lab's 2024 report. The 2028
// midpoint represents its 6.7%-12.0% scenario range, not a false point forecast.
// https://www1.eere.energy.gov/manufacturing/datacenters/pdfs/chp_data_centers.pdf
// https://eta-publications.lbl.gov/sites/default/files/2024-12/lbnl-2024-united-states-data-center-energy-usage-report_1.pdf
const US_DATA_CENTER_SHARES: readonly [number, number][] = [
  [1999, 0],
  [2000, 0.008],
  [2006, 0.015],
  [2014, 0.015],
  [2018, 0.019],
  [2023, 0.044],
  [2028, 0.0935],
  [2035, 0.14],
];

const REGION_DATA_CENTER_FACTOR: Record<string, number> = {
  "North America": 0.9,
  "South America": 0.35,
  Europe: 0.75,
  Africa: 0.2,
  "Middle East": 0.45,
  "Central Asia": 0.2,
  Caucasus: 0.2,
  "South Asia": 0.45,
  "East Asia": 0.9,
  "Southeast Asia": 0.65,
  Oceania: 0.7,
};

// Berkeley Lab identifies Virginia as the largest U.S. data-center load, followed by California
// and Texas. Multipliers turn that ordinal real-world finding into game-scale local exposure;
// they are deliberately capped below rather than presented as measured state shares.
const US_DATA_CENTER_FACTOR: Record<string, number> = {
  VA: 2.8,
  CA: 1.35,
  TX: 1.35,
};

const RUST_BELT = new Set(["MI", "OH", "PA", "WV"]);
const HIGH_GROWTH_US = new Set(["AZ", "FL", "GA", "NC", "TX"]);

function interpolate(
  points: readonly [number, number][],
  year: number,
): number {
  if (year < points[0][0]) {
    return points[0][1];
  }
  for (let i = 1; i < points.length; i++) {
    const [nextYear, nextValue] = points[i];
    const [previousYear, previousValue] = points[i - 1];
    if (year <= nextYear) {
      const progress = (year - previousYear) / (nextYear - previousYear);
      return previousValue + (nextValue - previousValue) * progress;
    }
  }
  const [lastYear, lastValue] = points[points.length - 1];
  // Long-range uncertainty is high. Taper the post-2035 curve and keep one category from taking
  // over the whole grid even in century-long sandbox games.
  return Math.min(0.2, lastValue * pow(1.02, year - lastYear));
}

/** Fraction of local demand represented by Data Centers before the other sector trends apply. */
export function dataCenterLoadShare(
  year: number,
  location?: LocationType,
): number {
  const national = interpolate(US_DATA_CENTER_SHARES, year);
  if (location?.country === "United States") {
    return Math.min(
      0.25,
      national * (US_DATA_CENTER_FACTOR[location.admin || ""] || 1),
    );
  }
  const regional = REGION_DATA_CENTER_FACTOR[location?.region || ""] || 0.5;
  return Math.min(0.2, national * regional);
}

function growthFor(location?: LocationType): GrowthProfile {
  const profile = {
    ...(REGION_GROWTH[location?.region || ""] || DEFAULT_GROWTH),
  };
  if (location?.country !== "United States") {
    return profile;
  }
  const admin = location.admin || "";
  if (RUST_BELT.has(admin)) {
    return {
      Residential: -0.005,
      Commercial: -0.003,
      Industrial: -0.015,
      Transportation: profile.Transportation,
    };
  }
  if (HIGH_GROWTH_US.has(admin)) {
    return {
      Residential: 0.01,
      Commercial: 0.015,
      Industrial: 0.01,
      Transportation: profile.Transportation,
    };
  }
  if (admin === "CA") {
    return {
      Residential: -0.002,
      Commercial: 0.005,
      Industrial: -0.006,
      Transportation: 0.04,
    };
  }
  return profile;
}

export type ClimateLoadArchetypeType =
  "cooling" | "mixed" | "electric-heating" | "fuel-heating";

// Heating/cooling degree approaches use a comfortable-weather reference rather than the old
// minimum near 5 C. EIA commonly uses 65 F (18.3 C):
// https://www.eia.gov/energyexplained/units-and-calculators/degree-days.php
// Regional heating fuels matter: electric heating is more prevalent in the U.S. South;
// cold northern states often heat with fuels rather than electricity:
// https://www.eia.gov/todayinenergy/detail.php?id=55940
// These four rounded response curves are authored archetypes, not measured city load models.
// Location chooses automatically; a student does not need to specify heating equipment.
const CLIMATE_LOAD_RESPONSES = {
  cooling: { heating: 1.8, cooling: 7.4, coolingSquared: 0.22 },
  mixed: { heating: 1, cooling: 7, coolingSquared: 0.2 },
  "electric-heating": { heating: 2.5, cooling: 4, coolingSquared: 0.12 },
  "fuel-heating": { heating: 0.8, cooling: 6, coolingSquared: 0.18 },
} as const;

// IEA describes substantial heat-pump use in these countries. This grouping is
// intentionally coarse and fixed over game history, not a historical equipment census:
// https://www.iea.org/reports/the-future-of-heat-pumps/executive-summary
const ELECTRIC_HEATING_COUNTRIES = new Set([
  "Norway",
  "Sweden",
  "Finland",
  "France",
]);
// Countries whose winters are carried by district heat, gas stoves or coal rather than by the
// grid. Their electric load still peaks in summer, so the cooling response stays high while the
// heating one does not. Relevant to Central Asia and the Caucasus, which until this catalogue
// were grouped with the Gulf and treated as cooling-only despite continental winters.
const FUEL_HEATING_COUNTRIES = new Set([
  "Armenia",
  "Belarus",
  "Georgia",
  "Kazakhstan",
  "Kyrgyzstan",
  "Mongolia",
  "Russia",
  "Tajikistan",
  "Turkmenistan",
  "Ukraine",
  "Uzbekistan",
]);
const COOLING_US_STATES = new Set([
  "AL",
  "AR",
  "AZ",
  "FL",
  "GA",
  "HI",
  "LA",
  "MS",
  "NM",
  "SC",
  "TX",
]);
const FUEL_HEATING_US_STATES = new Set([
  "CT",
  "IA",
  "IL",
  "IN",
  "MA",
  "ME",
  "MI",
  "MN",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NY",
  "OH",
  "PA",
  "RI",
  "SD",
  "VT",
  "WI",
]);

export function climateLoadArchetype(
  location?: LocationType,
): ClimateLoadArchetypeType {
  if (!location) return "mixed";
  if (location.country === "United States") {
    if (COOLING_US_STATES.has(location.admin || "")) return "cooling";
    if (FUEL_HEATING_US_STATES.has(location.admin || "")) return "fuel-heating";
  }
  if (Math.abs(location.lat) < 25 || location.region === "Middle East")
    return "cooling";
  if (ELECTRIC_HEATING_COUNTRIES.has(location.country || ""))
    return "electric-heating";
  if (FUEL_HEATING_COUNTRIES.has(location.country || "")) return "fuel-heating";
  return "mixed";
}

/** Weather-driven watts per customer; the other daily and sector patterns remain separate. */
export function temperatureDemandWattsPerCustomer(
  temperatureC: number,
  location?: LocationType,
): number {
  const response = CLIMATE_LOAD_RESPONSES[climateLoadArchetype(location)];
  const heatingDegrees = Math.max(0, 18 - temperatureC);
  const coolingDegrees = Math.max(0, temperatureC - 18);
  return (
    20 +
    response.heating * heatingDegrees +
    response.cooling * coolingDegrees +
    response.coolingSquared * coolingDegrees ** 2
  );
}

function hourShape(type: DemandTypeNameType, minuteOfDay: number): number {
  const hour = minuteOfDay / 60;
  const peak = (at: number, width: number) =>
    exp(-pow(hour - at, 2) / (2 * width * width));
  switch (type) {
    case "Residential":
      return 0.65 + 0.45 * peak(7, 1.8) + 0.75 * peak(19, 2.5);
    case "Commercial":
      return 0.35 + 0.9 * peak(13, 4.5);
    case "Industrial":
      return 1;
    case "Transportation":
      return 0.55 + 0.8 * peak(1, 3) + 0.55 * peak(21, 2.5);
    case "Data Centers":
      return 1;
    // A concentrator or a hoist runs the shift it is given, and the large mines run all of
    // them. Flat, for the same reason Industrial is.
    case "Mining":
      return 1;
  }
}

function calculateHourShapes(minuteOfDay: number): Readonly<DemandByTypeType> {
  const shapes = {} as DemandByTypeType;
  DEMAND_TYPES.forEach((type) => {
    shapes[type] = hourShape(type, minuteOfDay);
  });
  return shapes;
}

// Daily shapes never depend on a run, location or forecast horizon. Compute the fixed tick
// grid once; arbitrary-minute callers still use the exact formula without snapping time.
const DAILY_SHAPES = Array.from({ length: TICKS_PER_DAY }, (_, i) =>
  calculateHourShapes(i * TICK_MINUTES),
);

function hourShapesAt(minuteOfDay: number): Readonly<DemandByTypeType> {
  const index = minuteOfDay / TICK_MINUTES;
  return Number.isInteger(index) && index >= 0 && index < DAILY_SHAPES.length
    ? DAILY_SHAPES[index]
    : calculateHourShapes(minuteOfDay);
}

/**
 * Splits baseline demand into five end uses and applies their local trajectories.
 *
 * The starting instant is normalised back to baselineDemandW, so authored scenarios retain their
 * tuned opening balance. After that, the components can diverge and their sum becomes the actual
 * demand used by dispatch, finance, blackout logic, and the Insights forecast.
 */
export function demandByTypeAt(
  baselineDemandW: number,
  date: DateType,
  startingYear: number,
  location?: LocationType,
  loadAdditions: readonly ScenarioLoadAdditionType[] = [],
): DemandByTypeType {
  const mix = REGION_MIX[location?.region || ""] || DEFAULT_MIX;
  const growth = growthFor(location);
  const shapes = hourShapesAt(date.minuteOfDay);
  // An authored absolute schedule describes the whole scenario-specific data-center load. The
  // generic regional curve is removed unless the schedule explicitly adds a marginal campus.
  const hasAuthoredDataCenters = loadAdditions.some(
    (addition) =>
      addition.demandType === "Data Centers" && !addition.supplementsBackground,
  );
  const startDataCenters = hasAuthoredDataCenters
    ? 0
    : dataCenterLoadShare(startingYear, location);
  // date.percentOfYear deliberately substitutes 0.00001 at midnight on New Year's Day for
  // legacy chart math. Elapsed game minutes give this model a true zero at scenario start.
  const currentYear = startingYear + date.minute / (DAYS_PER_YEAR * 24 * 60);
  const years = Math.max(0, currentYear - startingYear);
  const currentDataCenters = hasAuthoredDataCenters
    ? 0
    : dataCenterLoadShare(currentYear, location);
  const nonDataCenterScale = 1 - startDataCenters;

  const startingWeights = {
    Residential: nonDataCenterScale * mix.Residential,
    Commercial: nonDataCenterScale * mix.Commercial,
    Industrial: nonDataCenterScale * mix.Industrial,
    Transportation: nonDataCenterScale * mix.Transportation,
    "Data Centers": startDataCenters,
    // There is no generic mining curve to open against, the way there is for Data Centers.
    // A scenario that has mines on its grid says so with a schedule, and that load arrives
    // below as an absolute addition rather than as a share of the customer baseline.
    Mining: 0,
  } satisfies Record<DemandTypeNameType, number>;
  const shapedStartingTotal = DEMAND_TYPES.reduce(
    (sum, type) => sum + startingWeights[type] * shapes[type],
    0,
  );

  const result = {} as DemandByTypeType;
  DEMAND_TYPES.forEach((type) => {
    const shape = shapes[type];
    if (type === "Data Centers") {
      // When a game begins before 2000 there is no starting component to grow, so introduce the
      // later load directly. Otherwise scale the opening component along the measured curve.
      const weight =
        startDataCenters > 0
          ? startingWeights[type] * (currentDataCenters / startDataCenters)
          : currentDataCenters;
      result[type] = (baselineDemandW * weight * shape) / shapedStartingTotal;
      return;
    }
    if (type === "Mining") {
      // Opens at nothing and has no regional trajectory to follow. A mine's load is whatever
      // the scenario's schedule says it is, applied below, and it does not drift with the
      // region's households in between.
      result[type] = 0;
      return;
    }
    result[type] =
      (baselineDemandW *
        startingWeights[type] *
        shape *
        pow(1 + growth[type], years)) /
      shapedStartingTotal;
  });
  if (years === 0) {
    const openingTotal = DEMAND_TYPES.reduce(
      (sum, type) => sum + result[type],
      0,
    );
    // Besides preserving authored balance conceptually, keep it bit-for-bit stable. A few UI
    // calculations compare the change in reserve capacity after adding a plant, and a residual
    // fraction of a watt from summing the components should not leak into that. Mining is never
    // the sink: it opens at zero, and pushing the residual there would invent a mine.
    result[hasAuthoredDataCenters ? "Commercial" : "Data Centers"] +=
      baselineDemandW - openingTotal;
  }
  loadAdditions.forEach((addition) => {
    result[addition.demandType] += scheduledLoadAdditionWAt(addition, date);
  });
  return result;
}

/** Instantaneous absolute load for one authored schedule. */
export function scheduledLoadAdditionWAt(
  addition: ScenarioLoadAdditionType,
  date: DateType,
): number {
  const startsMonth = addition.startsMonth || 1;
  const scheduledMonth = addition.startsYear * 12 + startsMonth - 1;
  const currentMonth = date.year * 12 + date.monthNumber - 1;
  if (currentMonth < scheduledMonth) {
    return 0;
  }
  // A constant utilization models data-center baseload without inventing a winter peak.
  if (addition.demandType === "Data Centers") {
    return (
      Math.max(0, addition.peakW) *
      Math.max(0, Math.min(1, addition.loadFactor))
    );
  }
  // The game represents each month with one detailed day. Sampling this twelve-point cosine over
  // a full game year has an exact mean of loadFactor and a maximum of peakW for mining.
  const monthOfCycle = (currentMonth - scheduledMonth) % 12;
  const loadFactor = Math.max(0, Math.min(1, addition.loadFactor));
  const multiplier =
    loadFactor + (1 - loadFactor) * cos((2 * Math.PI * monthOfCycle) / 12);
  return Math.max(0, addition.peakW) * multiplier;
}
